// Helpers puros para los criterios de desempate configurables (escalera
// ITF). Los cálculos de standings SIEMPRE vienen del backend; aquí solo hay
// presentación (etiquetas, texto explicativo) y edición de la config.

import type {
  StandingsPointsConfig,
  StandingsRulesConfig,
  StandingsTiebreaker,
} from "./api";

export const DEFAULT_STANDINGS_POINTS: StandingsPointsConfig = {
  win: 3,
  loss_completed: 1,
  walkover_loss: 0,
};

// Opciones reordenables del modo personalizado. El H2H NO está aquí: es fijo
// (encabeza la lista y solo aplica cuando quedan 2 empatados).
export const MOVABLE_TIEBREAKER_OPTIONS: StandingsTiebreaker[] = [
  "match_pct",
  "set_pct",
  "game_pct",
  "set_diff",
  "game_diff",
  "games_won",
  "fewest_games_lost",
];

export const TIEBREAKER_LABELS: Record<StandingsTiebreaker, string> = {
  h2h: "Enfrentamiento directo (aplica cuando quedan 2 empatados)",
  match_pct: "% de partidos ganados",
  set_pct: "% de sets ganados",
  game_pct: "% de juegos ganados",
  set_diff: "Diferencia de sets",
  game_diff: "Diferencia de juegos",
  games_won: "Juegos ganados",
  fewest_games_lost: "Menos juegos perdidos",
};

/** «enfrentamiento directo, % de partidos ganados, diferencia de sets…» para
 * la vista del jugador (mismas etiquetas que el formato, versión corta). */
export function tiebreakerSentence(tiebreakers: StandingsTiebreaker[]): string {
  return tiebreakers
    .map((key) => (key === "h2h" ? "enfrentamiento directo" : (TIEBREAKER_LABELS[key] ?? key).toLowerCase()))
    .join(", ");
}

// Escalera ITF exacta aprobada por Eduardo (2026-07-19), en español claro.
export const ITF_LADDER_EXPLANATION: string[] = [
  "1. Récord de partidos: victorias; si jugaron distinto número de partidos, % de partidos ganados.",
  "2. Empate entre dos → enfrentamiento directo (H2H).",
  "3. Empate entre tres o más → % de partidos ganados → % de sets ganados → % de juegos ganados (siempre de los completados, entre los empatados).",
  "4. Si la escalera reduce el grupo empatado a dos → se vuelve al H2H.",
  "5. Último recurso → sorteo (determinista y auditable).",
];

export const DETERMINISTIC_CLOSE_NOTE =
  "Cierre fijo al final: ranking inicial (seed) y después sorteo determinista — no se puede quitar.";

// Ayuda «?» de cada criterio (Eduardo 2026-10-01: «explicar cada uno con
// pop up ?»): corta, concreta y con un ejemplo chico. Mismo cálculo que el
// backend (standings/tiebreakers.py): todo se cuenta solo con partidos
// terminados y solo ENTRE los empatados; si nunca jugaron entre ellos, con
// todos sus partidos.
export const TIEBREAKER_SCOPE_NOTE =
  "Se cuenta solo con partidos terminados y solo entre los jugadores empatados; si nunca jugaron entre ellos, con todos sus partidos.";

export const TIEBREAKER_HELP: Record<StandingsTiebreaker, string> = {
  h2h: "Si quedan exactamente 2 empatados, queda arriba quien ganó el partido entre ellos. Ej.: Ana y Bea van 2-1 y Ana le ganó a Bea → Ana queda arriba.",
  match_pct: "Partidos ganados entre partidos jugados. Ej.: quien ganó 2 de 3 (67 %) queda arriba de quien ganó 1 de 2 (50 %).",
  set_pct: "Sets ganados entre sets jugados. Ej.: 4 de 6 (67 %) queda arriba de 3 de 6 (50 %).",
  game_pct: "Juegos ganados entre juegos jugados. Ej.: 30 de 50 (60 %) queda arriba de 27 de 50 (54 %).",
  set_diff: "Sets ganados menos sets perdidos. Ej.: 5-2 (+3) queda arriba de 4-3 (+1).",
  game_diff: "Juegos ganados menos juegos perdidos. Ej.: 36-20 (+16) queda arriba de 30-25 (+5).",
  games_won: "Total de juegos ganados. Ej.: quien ganó 36 juegos queda arriba de quien ganó 30.",
  fewest_games_lost: "Queda arriba quien perdió menos juegos. Ej.: quien perdió 18 queda arriba de quien perdió 24.",
};

export const RECORD_HELP =
  "Antes que cualquier desempate cuenta el récord: más victorias arriba; si jugaron distinto número de partidos, el % de partidos ganados. Ej.: 3-0 queda arriba de 2-1.";

export const H2H_HELP =
  "Solo cuando quedan exactamente 2 empatados (o cuando la lista reduce el empate a 2): queda arriba quien ganó el partido entre ellos. Ej.: Ana y Bea van 2-1 y Ana le ganó a Bea → Ana queda arriba. Con 3 o más se usa la lista.";

export const CLOSE_HELP =
  "Si después de todo siguen empatados: primero el mejor ranking inicial (seed); si no hay seed, un sorteo automático que siempre da el mismo resultado y se puede revisar.";

/** Ayuda de cada renglón de la escalera ITF (mismo orden que
 * ITF_LADDER_EXPLANATION). */
export const ITF_LADDER_HELP: string[] = [
  RECORD_HELP,
  H2H_HELP,
  "Con 3 o más empatados, en este orden: % de partidos ganados (ganó 2 de 3 = 67 % queda arriba de 1 de 2 = 50 %), luego % de sets ganados (4 de 6 = 67 % arriba de 3 de 6 = 50 %) y luego % de juegos ganados (30 de 50 = 60 % arriba de 27 de 50 = 54 %). " +
    TIEBREAKER_SCOPE_NOTE,
  "En cuanto uno de esos criterios separa al grupo y quedan solo 2 empatados, se decide con el partido entre esos dos (H2H).",
  CLOSE_HELP,
];

export type StandingsRulesKind = "legacy" | "itf" | "points" | "custom";

/** Liga por puntos: el primer paso son los puntos, no el récord (el
 * servidor ordena solo por puntos y la escalera rompe empates de puntos). */
export const POINTS_FIRST_LINE =
  "1. Puntos: la suma de puntos ordena la tabla; el récord (W-L) no se usa aparte.";
export const POINTS_FIRST_HELP =
  "En liga por puntos manda la suma de puntos y el récord no cuenta aparte. Ej.: con 3 por victoria y 1 por derrota jugada, 2-0 (6 pts) queda abajo de 1-4 (7 pts). Si empatan en puntos, se usa el resto de esta escalera.";

/** Renglones de la escalera con su ayuda según el tipo de reglas. */
export function itfLadderSteps(kind: StandingsRulesKind): Array<{ line: string; help: string }> {
  return ITF_LADDER_EXPLANATION.map((line, index) =>
    index === 0 && kind === "points"
      ? { line: POINTS_FIRST_LINE, help: POINTS_FIRST_HELP }
      : { line, help: ITF_LADDER_HELP[index] },
  );
}

export function standingsRulesKind(
  rules: StandingsRulesConfig | null | undefined,
): StandingsRulesKind {
  if (!rules || rules.mode === "legacy") return "legacy";
  if (rules.points) return "points";
  return rules.mode === "custom" ? "custom" : "itf";
}

export function itfStandingsRules(): StandingsRulesConfig {
  return { mode: "itf", points: null };
}

export function pointsLeagueStandingsRules(
  points: StandingsPointsConfig = DEFAULT_STANDINGS_POINTS,
): StandingsRulesConfig {
  return { mode: "itf", points: { ...points } };
}

export function customStandingsRules(
  tiebreakers?: StandingsTiebreaker[],
): StandingsRulesConfig {
  const movable = (tiebreakers ?? ["match_pct", "set_pct", "game_pct"]).filter(
    (criterion) => criterion !== "h2h",
  );
  // H2H fijo al frente: el backend lo antepone igual si faltara.
  return { mode: "custom", points: null, tiebreakers: ["h2h", ...movable] };
}

export function movableTiebreakers(
  rules: StandingsRulesConfig | null | undefined,
): StandingsTiebreaker[] {
  return (rules?.tiebreakers ?? []).filter((criterion) => criterion !== "h2h");
}

/** Mueve un criterio ↑/↓ dentro de la lista reordenable (inmutable). */
export function moveTiebreaker(
  list: StandingsTiebreaker[],
  index: number,
  direction: -1 | 1,
): StandingsTiebreaker[] {
  const target = index + direction;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/**
 * La columna PTS solo se muestra cuando el backend lo indica: legacy y liga
 * por puntos la muestran; ITF/custom sin puntos la ocultan. En modo legacy
 * el backend OMITE la bandera (respuesta histórica exacta): la ausencia se
 * trata como legacy (visible) igual que los payloads viejos.
 */
export function showPointsColumn(
  payload: { points_enabled?: boolean } | null | undefined,
): boolean {
  // Regla de producto (Eduardo 2026-07-22): la columna PTS solo existe si el
  // torneo declara liga por puntos (points_enabled === true). En tenis no hay
  // empates: para legacy/ITF/custom sin puntos, PTS es ruido y se oculta en
  // TODAS las superficies (pantalla, PDF, previews) por igual.
  return payload?.points_enabled === true;
}

/**
 * Vista por grupo: históricamente NO lleva columna PTS. Solo se muestra
 * cuando el scope del grupo declara points_enabled=true (liga por puntos);
 * la ausencia de la bandera (legacy) e ITF/custom sin puntos la ocultan.
 */
export function showPointsColumnInGroupView(
  scope: { points_enabled?: boolean } | null | undefined,
): boolean {
  // Misma regla que showPointsColumn desde 2026-07-22 (antes la vista general
  // era laxa con payloads legacy y la de grupo estricta — Eduardo unificó).
  return showPointsColumn(scope);
}

/**
 * Grupo (scope de reglas) del participante dentro del payload de standings.
 * "Mi posición" y las superficies por jugador deben leer las banderas de LA
 * CATEGORÍA DEL PARTICIPANTE: la bandera root es ambigua en torneos con
 * reglas mixtas por categoría.
 */
export function participantGroupScope<
  T extends { standings: Array<{ participant_id: number }> },
>(
  groups: readonly T[] | null | undefined,
  participantId: number | null | undefined,
): T | null {
  if (!groups || participantId === null || participantId === undefined) {
    return null;
  }
  return (
    groups.find((group) =>
      group.standings.some((row) => row.participant_id === participantId),
    ) ?? null
  );
}

/**
 * Relación obligatoria de la liga por puntos, idéntica a la del backend:
 * win > loss_completed >= walkover_loss. Devuelve EL MISMO texto que el 409
 * del backend (para validar en el form antes de enviar), o null si es
 * válida.
 */
export function standingsPointsRelationError(
  points: StandingsPointsConfig | null | undefined,
): string | null {
  if (!points) return null;
  if (points.walkover_loss > points.loss_completed) {
    return "standings_rules.points: walkover_loss no puede superar loss_completed";
  }
  if (points.loss_completed >= points.win) {
    return "standings_rules.points: la victoria debe valer más que la derrota";
  }
  return null;
}

/** ¿Ya hay resultados en estas filas? (alguien jugó al menos un partido). */
export function rowsHaveResults(
  rows: ReadonlyArray<{ matches_played?: number | null }> | null | undefined,
): boolean {
  return (rows ?? []).some((row) => (row.matches_played ?? 0) > 0);
}

/**
 * ¿La categoría (o el torneo, sin filtro) ya empezó? (Eduardo 2026-10-01:
 * «aquí todavía no empieza el torneo… no deberían tener colores de
 * lugares»). Empieza con el primer resultado capturado: el resumen del
 * backend (`matches_with_results`, acotado a la categoría con ?division),
 * cualquier fila con partidos jugados o una escalera importada con datos
 * legacy. Antes de eso no hay lugares, clasificados ni Rk Gral.
 */
export function standingsHaveResults(
  payload:
    | {
        summary?: { matches_with_results?: number | null } | null;
        ranking?: ReadonlyArray<{ matches_played?: number | null }> | null;
        groups?: ReadonlyArray<{ standings: ReadonlyArray<{ matches_played?: number | null }> }> | null;
        legacy_stats?: { available?: boolean } | null;
      }
    | null
    | undefined,
): boolean {
  if (!payload) return false;
  if ((payload.summary?.matches_with_results ?? 0) > 0) return true;
  if (payload.legacy_stats?.available) return true;
  if (rowsHaveResults(payload.ranking)) return true;
  return (payload.groups ?? []).some((group) => rowsHaveResults(group.standings));
}

/**
 * Igual que standingsHaveResults pero por CATEGORÍA: el payload del jugador
 * trae todo el torneo y una categoría puede ir adelante de otra. Regresa
 * «¿ya empezó la categoría de esta fila?». Filas sin division_id caen en una
 * sola cubeta (torneos viejos): ahí equivale al criterio de todo el torneo.
 */
export function divisionStartedChecker(
  payload:
    | {
        ranking?: ReadonlyArray<{ matches_played?: number | null; division_id?: number | null }> | null;
        groups?: ReadonlyArray<{
          standings: ReadonlyArray<{ matches_played?: number | null; division_id?: number | null }>;
        }> | null;
        legacy_stats?: { available?: boolean } | null;
      }
    | null
    | undefined,
): (divisionId: number | null | undefined) => boolean {
  if (!payload) return () => false;
  if (payload.legacy_stats?.available) return () => true;
  const started = new Set<number | null>();
  const rows = [...(payload.ranking ?? []), ...(payload.groups ?? []).flatMap((group) => group.standings)];
  for (const row of rows) {
    if ((row.matches_played ?? 0) > 0) started.add(row.division_id ?? null);
  }
  return (divisionId) => started.has(divisionId ?? null);
}

/**
 * Celda «Rk Ini» (Eduardo 2026-10-01: «rank inicial debería de tener o los
 * puntos en este caso o S/R»). En una categoría rankeada por puntos (alguien
 * tiene puntos capturados en el paso Ranking) muestra los puntos del
 * jugador; si no, su seed. Sin ninguno: «S/R» (sin ranking), nunca «—».
 */
export function initialRankDisplay(
  seed: number | null | undefined,
  rankingPoints: number | null | undefined,
  rankedByPoints: boolean,
): string {
  if (rankedByPoints) return (rankingPoints ?? 0) > 0 ? `${rankingPoints} pts` : "S/R";
  return seed != null ? String(seed) : "S/R";
}
